/*
 *  Copyright (c) Qwoach LLC 2020. All rights reserved.
 *  Unauthorized copying of this file, via any medium is strictly prohibited
 *  Proprietary and confidential
 *  Created by Artem Bogush <artem@qwoach.com> on 7/10/2020
 */

import BaseProfile from "@shared/Profile/BaseProfile.js";

import ProgramInfo from "@/core/js/model/Program/ProgramInfo";
import {Type, plainToClass} from "@qwoach/class-transformer-wrapper";
import SocialMediaContainer from "@shared/Profile/SocialMediaContainer";
import PersonalInfoContainer from "@shared/Profile/PersonalInfoContainer";
import {FilterableProperty, SyncProperty} from "@shared/Filterable/PropertyDecorators";
import TagItem from "@shared/Tags/TagItem";
import ContactInfo from "@shared/Profile/ContactInfo";
import AddressItem from "@shared/Profile/AddressItem";
import CoachingPackageRef from "@shared/CoachingPackage/CoachingPackageRef";
import ToolsSettings from "@shared/Profile/ToolsSettings";
import TimeZoneItem from "@shared/Profile/TimeZoneItem";
import {logError} from "@/utils/helpers.js";
import ClientAccessRuleItem from "@shared/Profile/ClientAccessRuleItem.js";
import {ProfileTypeEnum} from "@shared/Profile/ProfileTypeEnum.js";
import {GenogramToolId, IAOTRCToolId} from "@shared/Profile/CoachingToolsConstants.js";
import {ObjectTypeEnum} from "@shared/Invoice/ObjectTypeEnum";
import {ClientStage} from "@shared/Profile/ClientStage";

let CONJUNCTION_MAX_LENGTH = 30 // firestore supports up to 30 array-contains-any as of Aug 2023

export default class ClientProfile extends BaseProfile{

	profileTitle: string = "client"

	consentAccepted: string = null
	consentText: string = null

	@Type(() => ClientAccessRuleItem)
	accessRuleItem: ClientAccessRuleItem = new ClientAccessRuleItem() //Only writable by account owner; syncs back to userprofile custom claims


	@FilterableProperty()
	company: string = null

	@SyncProperty({targetProp: "company", transform: item => item.toLowerCase()})
	company_casefolded: string = null

	@Type(() => AddressItem)
	@FilterableProperty(address => address.toString())
	primaryAddress: AddressItem = null

	occupation: string = null
	relationshipStatus: string = null

	coachId: string = null; //refers to the owner of the Client profile
	clientId: string = null; //deprecated since v7. Use userProfileUid instead

	programIds: string[] = [];


	isEnabled: boolean = true

	@Type(() => ProgramInfo)
	programInfoArray: ProgramInfo[] = []; //legacy

	@Type(() => CoachingPackageRef)
	coachingPackageRefs: CoachingPackageRef[] = [];

	@Type(() => SocialMediaContainer)
	socialMedia: SocialMediaContainer = new SocialMediaContainer();

	@Type(() => PersonalInfoContainer)
	personalInfo: PersonalInfoContainer = new PersonalInfoContainer();

	// tagTitleArray: string[] = []; //Deprecated. needed for db filtering using "array-contains"

			// clientStatus: ClientStatus = ClientStatus.CLIENT
	clientStage: ClientStage = ClientStage.CLIENT

	attachedWorkflowId: string = null
	attachedWorkflowStateId: string = null
	workflowTitle: string = null

	workflowStageIds: string[] = []

	intakeFormIds = {}

	signedTemplateIds: string[] = []

	@Type(() => ContactInfo)
	emergencyContacts: ContactInfo[] = []

	@Type(() => ToolsSettings)
	toolsSettings: ToolsSettings = new ToolsSettings()



	constructor(name = "", email = "") {
		super(name, email, ProfileTypeEnum.CLIENT);
		this.syncRuleItemMyIdForOwner()
		this.toolsSettings.enableCoachingTool(GenogramToolId)//enable by default since this is the most of our client at the moment
	}

	getCurrentSchemaVersion() {
		return 10
	}

	isClientProfile(){
		return true
	}

	recentProgramInfo(): ProgramInfo{
		return this.programInfoArray[this.programInfoArray.length - 1] || new ProgramInfo("");
	}

	programInfoByProgramId(programId){
		for(let programInfo of this.programInfoArray) {
			if (programId && programInfo.programId === programId) {
				return programInfo;
			}
		}
		return null;
	}

	isActiveClient(){
		return this.clientStage === ClientStage.CLIENT && this.isEnabled
	}
	isStageClient(){
		return this.clientStage === ClientStage.CLIENT
	}
	isStageProspect(){
		return this.clientStage === ClientStage.PROSPECT
	}

	programInfoById(programInfoId){
		for(let programInfo of this.programInfoArray) {
			if (programInfoId && programInfo.id === programInfoId) {
				return programInfo;
			}
		}
		return null;
	}


	getProgramInfo(programId){
		return this.programInfoByProgramId(programId);
	}

	getProgramInfoStatus(programId): string{
		let programInfo = this.getProgramInfo(programId);
		if (programInfo){
			return programInfo.statusFormatted();
		}
		return "";
	}

	markAsProspect(){
		this.clientStage = ClientStage.PROSPECT
	}

	markAsClient(){
		this.clientStage = ClientStage.CLIENT
	}

	addProgramInfo(programInfo: ProgramInfo){
		if(programInfo && programInfo.programId){
			this.programInfoArray.pushUnique(programInfo, item => item.programId === programInfo.programId)
			this.programIds.pushUnique(programInfo.programId)
		}
	}
	addProgramId(programId){
		this.programIds.pushUnique(programId)
	}

	addCoachingPackageRef(coachingPackageRef){
		this.coachingPackageRefs.push(coachingPackageRef)
	}

	getSharedIdsObject(){
		return {
			clientId: this.id,
			groupIds: this.getGroupSharedIdsArray()
		}
	}
	getSharedIdsArray(){
		let groupRefIds = this.getGroupSharedIdsArray()
		let res = [this.id, ...groupRefIds]
		this.capConjunctionLength(res)
		return res
	}

	getGroupSharedIdsArray(){
		let groupRefs = this.coachingPackageRefs.filter(ref => ref.isGroupPackage())
		let res = groupRefs.map(ref => ref.id)
		this.capConjunctionLength(res)
		return res
	}
	findPackageRefById(packageId){
		let res = this.coachingPackageRefs.filter(ref => ref.id === packageId)
		return res[0]
	}
	capConjunctionLength(res){
		if(res.length > CONJUNCTION_MAX_LENGTH){
			logError(new Error("getSharedIdsArray: Length exceeded max: " + res.length + ". Force reducing back to " + CONJUNCTION_MAX_LENGTH))
			res.length = CONJUNCTION_MAX_LENGTH
		}
	}


	mailToHref(){
		return "mailto://" + this.email;
	}


	migrateSchema(oldVersion, newVersion) {
		super.migrateSchema(oldVersion, newVersion)

		if(oldVersion < 2){
			let tags = []
			if(this.tagArray){
				this.tagArray.forEach(tag => {
					let tagItem = tag
					if(typeof tag === "string"){
						tagItem = new TagItem(tag)
					}
					tags.push(tagItem)
				})
				this.tagItemsArray = tags
				this.tagTitleArray = this.tagArray
				this.tagArray = []
			}
		}
		if(oldVersion < 3){
			this.dateOfBirth = this.birthday || null
		}
		if(oldVersion < 4){
			if(this.timezone){
				this.timeZoneItem = new TimeZoneItem(this.timezone)
			}
		}
		if(oldVersion < 5){
			if((this.homeAddress || this.address) && !this.primaryAddress){
				this.primaryAddress = plainToClass(AddressItem, this.homeAddress || this.address)
			}
		}
		if(oldVersion < 6){
			// this version was skipped accidentally, so no changes here.
		}
		if(oldVersion < 7){

			this.userProfileUid = this.clientId

			if(this.clientJournalInviteStatus){
				this.portalInviteStatus = this.clientJournalInviteStatus
			}

			// one of the change here is updated searchKeywords
			// from array to object inside FilterableItem
			// which doesn't require any code - just save to the database

			// another change is to use sharedWithIds as centralized place for all shared
			this.addSharedWithById(this.coachId)
		}
		if(oldVersion < 8){
			//this migration was performed in the backend under migrateTeamFeature migration
		}
		if(oldVersion < 9){
			this.toolsSettings.enableCoachingTool(GenogramToolId)
			if(this.attachedWorkflowId){
				this.toolsSettings.enableCoachingTool(IAOTRCToolId)
			}
		}
		if(oldVersion < 10){
			// if(this.id !== this.accessRuleItem.myIdForOwner){
			// 	logError(new Error("ClientProfile.migrateSchema: id's are not the same. Syncing them. this.id = " + this.id + ", this.accessRuleItem.myIdForOwner = " + this.accessRuleItem.myIdForOwner))
			// }
			this.syncRuleItemMyIdForOwner()
		}
	}

	createPublicProfile(){
		let clientProfile = new ClientProfile()

		clientProfile.setId(this.id)
		clientProfile.clientId = this.userProfileUid

		clientProfile.name = this.name
		clientProfile.email = this.email
		clientProfile.clientStage = this.clientStage

		return clientProfile
	}


	syncToEntryItem(workflowEntryItem){
		workflowEntryItem.id = this.id
		workflowEntryItem.objectId = this.id
		workflowEntryItem.setObjectType(ObjectTypeEnum.CLIENT)
		workflowEntryItem.objectData.tagItemsArray = this.tagItemsArray
		workflowEntryItem.title = this.name
		workflowEntryItem.subtitle = this.email
	}

	syncFromEntryItem(workflowEntryItem){
		console.log("ClientProfile.syncFromEntryItem: workflowEntryItem = ", workflowEntryItem)
		this.name = workflowEntryItem.title
		this.email = workflowEntryItem.subtitle
		this.tagItemsArray = workflowEntryItem.objectData.tagItemsArray

		if(this.id !== workflowEntryItem.objectId){
			console.warn("ClientProfile.syncFromEntryItem: id's are not the same. Syncing them. this.id = ", this.id, ", workflowEntryItem.objectId = ", workflowEntryItem.objectId)
		}
		this.id = workflowEntryItem.objectId
	}



}
